import errorNames from '../constants/error-names';

const ABORT_EXECUTION = Symbol('ABORT_EXECUTION');

const createStopSignal = () => ({
  [ABORT_EXECUTION]: true,
});

const isStopSignal = (result) => result?.[ABORT_EXECUTION] === true;

const createAbortError = () => {
  const error = new Error('Staged handler execution aborted');
  error.name = errorNames.STAGED_HANDLER_ABORT_ERROR;
  return error;
};

const isAbortError = (error) => error.name === errorNames.STAGED_HANDLER_ABORT_ERROR;

const isEnrichedError = (error) => error.originalError !== undefined;

const enrichHandlerError = (error, { stage, chunkId, flowData }) => {
  if (isEnrichedError(error)) {
    return error;
  }

  const enriched = new Error(
    `Error in handler for stage "${String(stage)}" in chunk "${chunkId}": ${error.message}`
  );

  enriched.originalError = error;
  enriched.stage = stage;
  enriched.chunkId = chunkId;
  enriched.flowData = flowData;
  enriched.stack = `${enriched.stack}\nCaused by: ${error.stack}`;

  return enriched;
};

const defaultMergeResult = (current, result) => {
  if (result && typeof result === 'object') {
    return { ...current, ...result };
  }

  return current;
};

const runStageHandlers = async (handlers, options) => {
  const { stage, initialContext, flowData = {}, onMergeResult, getAdditionalContext } = options;

  let current = { ...flowData };
  const merge = onMergeResult || defaultMergeResult;

  const runHandler = async ({ handler, chunkId }) => {
    const context = {
      ...initialContext,
      ...(getAdditionalContext?.({ chunkId, stage }) || {}),
      stage,
      createStopSignal,
    };

    let result;
    try {
      result = await handler(current, context);
    } catch (error) {
      throw enrichHandlerError(error, {
        stage,
        chunkId,
        flowData: current,
      });
    }

    if (isStopSignal(result)) {
      throw createAbortError();
    }

    try {
      const merged = merge(current, result);

      if (merged && typeof merged === 'object') {
        current = { ...current, ...merged };
      }
    } catch (error) {
      throw enrichHandlerError(new Error(`Merge error: ${error.message}`), {
        stage,
        chunkId,
        flowData: current,
      });
    }
  };

  await handlers.reduce(async (previousPromise, handlerInfo) => {
    await previousPromise;

    try {
      await runHandler(handlerInfo);
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }

      throw enrichHandlerError(error, {
        stage,
        chunkId: handlerInfo.chunkId,
        flowData: current,
      });
    }
  }, Promise.resolve());

  return current;
};

export default runStageHandlers;
